/**
 * Live Section block: dynamic data with filtering
 * Fetches data once from backend, filters locally for performance.
 */

import { registerBlockRenderer, getBlockRenderer, getBlockRendererContext } from './registry';
import { escapeHtml, createBlockWrapper, createErrorState, createEmptyState } from './utils';
import type { LiveSectionBlockData } from './types';
import type { LiveDataItem, LiveSectionResponse } from '../../core/api-client';
import { inlineLoaderHTML } from '../../core/loading/activity-loader';

// =============================================================================
// LIVE SECTION BLOCK
// =============================================================================

registerBlockRenderer('live-section', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  const sectionData = data as LiveSectionBlockData;
  const ctx = getBlockRendererContext();

  const wrapper = createBlockWrapper('live-section');

  // Title
  if (sectionData.title) {
    const title = document.createElement('div');
    title.className = 'pms-live-section-title';
    title.textContent = sectionData.title;
    wrapper.appendChild(title);
  }

  // Filters container (will be populated after data fetch)
  const filtersWrapper = document.createElement('div');
  filtersWrapper.className = 'pms-live-section-filters';
  wrapper.appendChild(filtersWrapper);

  // Content container with loading state
  const contentEl = document.createElement('div');
  contentEl.className = 'pms-live-section-content';
  contentEl.innerHTML = `
    <div class="pms-live-section-loading">
      ${inlineLoaderHTML('Loading...')}
    </div>
  `;
  wrapper.appendChild(contentEl);

  container.appendChild(wrapper);

  // =========================================================================
  // LOCAL DATA & FILTERING (no API calls after initial fetch)
  // =========================================================================

  // Store ALL items from initial fetch
  let allItems: LiveDataItem[] = [];
  let availableFilters: LiveSectionResponse['filters'] = [];

  // Current filter state
  let currentFilters: Record<string, string> = sectionData.initialFilters || {};

  // Debounce timer for filter changes
  let filterDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Apply filters locally - NO API CALL
   */
  function applyLocalFilters(): LiveDataItem[] {
    if (Object.keys(currentFilters).length === 0) {
      return allItems;
    }

    return allItems.filter(item => {
      for (const [filterKey, filterValue] of Object.entries(currentFilters)) {
        if (!filterValue) continue;

        // Check category
        if (filterKey === 'category') {
          if (item.category?.toLowerCase() !== filterValue.toLowerCase()) {
            return false;
          }
          continue;
        }

        // Check subcategory
        if (filterKey === 'subcategory') {
          if (item.subcategory?.toLowerCase() !== filterValue.toLowerCase()) {
            return false;
          }
          continue;
        }

        // Check filterValues map
        const itemFilterValue = item.filterValues?.[filterKey];
        if (itemFilterValue === undefined) {
          return false;
        }
        if (itemFilterValue.toLowerCase() !== filterValue.toLowerCase()) {
          return false;
        }
      }
      return true;
    });
  }

  /**
   * Re-render items with current filters (LOCAL - no API call)
   */
  function rerenderWithLocalFilters(): void {
    const filtered = applyLocalFilters();
    renderItems(filtered, contentEl, onAction);
  }

  /**
   * Fetch data ONCE from API
   */
  async function fetchDataOnce(): Promise<void> {
    if (!ctx.apiClient || !ctx.workspaceId) {
      contentEl.innerHTML = createErrorState('This content is not available right now');
      return;
    }

    try {
      // Fetch ALL items (no filters, high limit)
      const response: LiveSectionResponse = await ctx.apiClient.getLiveSectionData(
        ctx.workspaceId,
        sectionData.id,
        {}, // No filters - get everything
        sectionData.limit || 100 // Get more items for local filtering
      );

      // Store all items for local filtering
      allItems = response.items;
      availableFilters = response.filters || [];

      // Render filters
      if (availableFilters.length > 0) {
        renderFilters(availableFilters);
      }

      // Apply initial filters and render
      rerenderWithLocalFilters();

    } catch (error) {
      console.error('Failed to fetch live section data:', error);
      contentEl.innerHTML = createErrorState('This content could not be loaded right now');
    }
  }

  /**
   * Render filter dropdowns
   */
  function renderFilters(filters: LiveSectionResponse['filters']): void {
    filtersWrapper.innerHTML = '';

    for (const filter of filters) {
      const filterGroup = document.createElement('div');
      filterGroup.className = 'pms-live-section-filter';

      const label = document.createElement('label');
      label.className = 'pms-live-section-filter-label';
      label.textContent = filter.label;
      filterGroup.appendChild(label);

      const select = document.createElement('select');
      select.className = 'pms-live-section-select';

      const defaultOption = document.createElement('option');
      defaultOption.value = '';
      defaultOption.textContent = `All ${filter.label}`;
      select.appendChild(defaultOption);

      for (const opt of filter.options) {
        const option = document.createElement('option');
        option.value = opt.value;
        option.textContent = opt.label;
        // Set selected if current filter matches
        if (currentFilters[filter.id] === opt.value) {
          option.selected = true;
        }
        select.appendChild(option);
      }

      select.addEventListener('change', () => {
        if (select.value) {
          currentFilters[filter.id] = select.value;
        } else {
          delete currentFilters[filter.id];
        }

        // Debounce filter changes to prevent UI jank from rapid selections
        if (filterDebounceTimer) {
          clearTimeout(filterDebounceTimer);
        }
        filterDebounceTimer = setTimeout(() => {
          filterDebounceTimer = null;
          // Guard: only rerender if container is still in DOM
          if (container.isConnected) {
            // LOCAL FILTER - no API call!
            rerenderWithLocalFilters();
          }
        }, 50);
      });

      filterGroup.appendChild(select);
      filtersWrapper.appendChild(filterGroup);
    }
  }

  // Fetch data ONCE on mount (filtering is local after this)
  fetchDataOnce();
});

/**
 * Render items grid in the content container.
 */
function renderItems(
  items: LiveDataItem[],
  contentEl: HTMLElement,
  onAction?: (value: string) => void
): void {
  contentEl.innerHTML = '';

  if (items.length === 0) {
    contentEl.innerHTML = createEmptyState('No items found', { compact: true });
    return;
  }

  const grid = document.createElement('div');
  grid.className = 'pms-cards-grid pms-live-cards-grid';

  for (const item of items) {
    const card = renderLiveDataCard(item, onAction);
    grid.appendChild(card);
  }

  contentEl.appendChild(grid);
}

/**
 * Render a live data item as a card.
 */
function renderLiveDataCard(
  item: LiveDataItem,
  onAction?: (value: string) => void
): HTMLElement {
  const card = document.createElement('div');
  card.className = 'pms-card pms-live-card';

  let html = '';

  // Image
  if (item.imageUrl) {
    html += `<img class="pms-card-image" src="${escapeHtml(item.imageUrl)}" alt="" loading="lazy" decoding="async" />`;
  }

  // Badge
  if (item.badge) {
    html += `<div class="pms-card-badge">${escapeHtml(item.badge)}</div>`;
  }

  // Title
  if (item.title) {
    html += `<div class="pms-card-title">${escapeHtml(item.title)}</div>`;
  }

  // Description
  if (item.description) {
    html += `<div class="pms-card-description">${escapeHtml(item.description)}</div>`;
  }

  // Price
  if (item.price) {
    html += `<div class="pms-card-price">${escapeHtml(item.price)}</div>`;
  }

  // Metadata
  if (item.metadata && Object.keys(item.metadata).length > 0) {
    html += '<div class="pms-card-metadata">';
    for (const [key, value] of Object.entries(item.metadata)) {
      if (value !== null && value !== undefined) {
        html += `<span class="pms-card-meta-item"><strong>${escapeHtml(key)}:</strong> ${escapeHtml(String(value))}</span>`;
      }
    }
    html += '</div>';
  }

  // Action button
  if (item.actionConfig) {
    const actionLabel = item.actionConfig.label || 'View';
    const actionValue = item.actionConfig.value || `view:${item.id}`;

    if (item.actionConfig.url) {
      html += `<a class="pms-card-action" href="${escapeHtml(item.actionConfig.url)}" target="_blank" rel="noopener">${escapeHtml(actionLabel)}</a>`;
    } else {
      html += `<button class="pms-card-action" data-action="${escapeHtml(actionValue)}">${escapeHtml(actionLabel)}</button>`;
    }
  }

  card.innerHTML = html;

  // Bind action button click
  const actionBtn = card.querySelector('.pms-card-action[data-action]') as HTMLButtonElement | null;
  if (actionBtn && onAction) {
    actionBtn.addEventListener('click', () => {
      const action = actionBtn.dataset['action'];
      if (action) onAction(action);
    });
  }

  return card;
}

// Also register for live_section format
registerBlockRenderer('live_section', (data, container, onAction) => {
  const renderer = getBlockRenderer('live-section');
  if (renderer) renderer(data, container, onAction);
});
